import React, { type FC, type ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadRemoteModule } from "@cw-elements/mfe-setup/load";
import { t } from "i18next";

import "./header.less";
import Logo from "../../assets/images/trialing-logo.svg";
import { useAuth } from "../../lib/auth/AuthContext";
import { SupportedLanguages } from "../../constants/supportedLanguages";
import i18n from "../../i18n";
import { useAppState } from "../../lib/appContext/AppContext";
import { type TopbarNavigationMFEProps } from "@cw-elements/mfe-setup/topbar-navigation";
import { appURLs } from "../../lib/appRoutes/appUrls";
import { useTrialsRouter } from "../custom-hooks/useTrialsRouter";
import { useProtocolsRouter } from "../custom-hooks/useProtocolsRouter";

const TopbarNavigation = React.lazy(async () => {
  try {
    const module = await loadRemoteModule<TopbarNavigationMFEProps>(
      "topbar-navigation/index"
    );
    return { default: module.default };
  } catch (error) {
    console.error("Failed to load TopbarNavigation module:", error);
    throw error;
  }
});

export const AppBarPlatform: FC = (): ReactElement => {
  const { user, logout, setLogoutStatus } = useAuth();
  const { stateValue, setStateValue } = useAppState();

  const {
    orgId: trialsOrgId,
    workspaceId: trialsWorkspaceId,
    farmId: trialsFarmId,
  } = useTrialsRouter();
  const {
    orgId: protocolsOrgId,
    workspaceId: protocolsWorkspaceId,
    farmId: protocolsFarmId,
  } = useProtocolsRouter();
  const orgId = trialsOrgId ?? protocolsOrgId ?? "";
  const farmId = trialsFarmId ?? protocolsFarmId ?? "";
  const selectedOrgWorkspaceId =
    trialsWorkspaceId ?? protocolsWorkspaceId ?? "";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMyCropwise = Boolean(pathname.includes(appURLs.MY_CROPWISE));
  const AppId = process.env.REACT_APP_TRIALING_APP_ID ?? "";

  const changeLanguage = (language: string): void => {
    i18n.changeLanguage(language).catch((error) => {
      console.error("Error", error);
    });
  };

  return (
    <TopbarNavigation
      userLanguage={i18n.language}
      id="topbar-navigation"
      userName={user?.name}
      userEmail={user?.email}
      onLogout={() => {
        setLogoutStatus(true);
        void logout();
      }}
      defaultItems={{
        selectedWorkspace: {
          value: selectedOrgWorkspaceId,
        },
        selectedOrganization: { value: orgId },
        selectedFarm: { value: farmId },
      }}
      baseRoute={""}
      productImage={{
        redirectUrl: "/",
        imageSrc: Logo,
        name: t(""),
      }}
      featureConfig={{
        hasSettingsMenu: true,
        hasProfileDropdown: true,
        hasDeckOfApps: true,
        hideEntitiesDropdown: isMyCropwise,
      }}
      settingsMenuConfig={() => ({
        headerSelector: "#topbar-navigation",
        workspaceId: selectedOrgWorkspaceId,
        orgId,
        propertyId: farmId,
      })}
      organizationDropdown={() => ({
        onSelect: (org: { value: any; label: string; workspaceId: string }) => {
          const matchedOrg = stateValue?.orgsData?.content?.find(
            (o: any) => o?.id === org.value
          );
          setStateValue({
            ...stateValue,
            orgValue: {
              ...matchedOrg,
              id: org.value,
              name: org.label,
              workspace_id: org.workspaceId,
            },
            farmValue: null,
            assigneeValue: null,
          });
        },
        goToEntity: () => {
          navigate(`${appURLs.MY_CROPWISE}`, {
            state: {
              stopRedirect: true,
            },
          });
        },
      })}
      farmDropdown={() => ({
        onSelect: (farm: { value: any; orgId: any; workspaceId?: string }) => {
          const matchedOrg = stateValue?.orgsData?.content?.find(
            (o: any) => o?.id === farm.orgId
          );
          const orgValue: any = {
            ...matchedOrg,
            id: farm.orgId,
            workspace_id: farm.workspaceId ?? stateValue.orgValue?.workspace_id,
          };
          const farmValue: any = { id: farm.value };
          setStateValue({
            ...stateValue,
            orgValue,
            farmValue,
            assigneeValue: null,
          });
          const workspaceId =
            farm.workspaceId ?? stateValue.orgValue?.workspace_id;
          const orgId = farm.orgId ?? stateValue.orgValue?.id;
          navigate(
            `${appURLs.TRIALS}/workspace/${workspaceId as string}/org/${
              orgId as string
            }/farm/${farm.value as string}`
          );
        },
        goToEntity: ({
          workspaceId,
          orgId,
        }: {
          workspaceId: string;
          orgId: string;
        }) => {},
      })}
      profileDropdownConfig={() => ({
        availableLanguages: SupportedLanguages.map((lang) => lang.value),
        availableUnitSystems: [],
        onLanguageChange: (lang: string) => {
          changeLanguage(lang);
        },
      })}
      appData={{
        name: "Cropwise Trialing",
        id: AppId,
      }}
    />
  );
};
